import {
  FocusEventHandler,
  ForwardedRef,
  KeyboardEventHandler,
  MouseEventHandler,
  forwardRef,
  useRef
} from "react";
import cx from "classnames";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

interface TextInputProps {
  id?: string;
  name: string;
  value?: string | number;
  onChangeValue?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  type?: string;
  errorMessage?: string;
  dataTestId?: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  min?: number;
  max?: number;
  size?: number;
  maxLength?: number;
  ariaLabel?: string;
  readOnly?: boolean;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  dataQa?: string;
  title?: string;
}
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      id,
      name,
      type = "text",
      value,
      onChangeValue,
      className,
      errorMessage,
      disabled,
      dataTestId,
      placeholder,
      required,
      autoFocus,
      autoComplete,
      onKeyDown,
      min,
      max,
      size,
      maxLength,
      ariaLabel,
      readOnly,
      onClick,
      onFocus,
      onBlur,
      dataQa,
      title
    }: TextInputProps,
    forwardedRef: ForwardedRef<HTMLInputElement>
  ) => {
    const ownRef = useRef<HTMLInputElement>(null);
    const ref = forwardedRef || ownRef;
    const useLegacyStyling = className?.split(/\s+/).includes("Input") ?? false;
    const input = (
      <input
        ref={ref}
        type={type}
        name={name}
        id={id || name}
        data-testid={dataTestId}
        data-qa={dataQa}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        min={min}
        max={max}
        size={size}
        maxLength={maxLength}
        aria-label={ariaLabel}
        readOnly={readOnly}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        title={title}
        className={cx(
          !useLegacyStyling && "Input",
          className,
          errorMessage && "has-error",
          errorMessage &&
            !useLegacyStyling &&
            "pr-10 text-error placeholder:text-red-300",
          disabled && "is-disabled"
        )}
        value={value}
        onChange={
          onChangeValue ? e => onChangeValue(e.target.value) : undefined
        }
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${name}-error` : undefined}
      />
    );

    if (!errorMessage) {
      return input;
    }

    return (
      <>
        <div
          className={cx(
            "relative",
            !useLegacyStyling && "rounded-md shadow-sm"
          )}
        >
          {input}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        </div>
        <p className="mt-2 text-sm text-red-600" id={`${name}-error`}>
          {errorMessage}
        </p>
      </>
    );
  }
);
TextInput.displayName = "TextInput";

export default TextInput;
